/**
 * Per-organization cache in localStorage. Single slot per org carrying both the signed
 * JWT (server-issued, used to skip GET /api/get/user_jwt round-trips) and the fullest
 * `user_data` we've seen for the org — usually built up from a form submit's
 * `response_data.returns.user_data`. Reload prefill draws on `user_data` so PII the
 * JWT itself doesn't carry (first_name, cell_phone, address, etc.) still pre-fills.
 *
 * Key: `data-hub-user-data:${organization_id}` — value JSON
 *      { jwt?, user_data?, created_at, updated_at, jwt_updated_at?, user_data_updated_at? }.
 * Both `jwt` and `user_data` are independently optional; writes preserve the field
 * they don't touch.
 */

import { normalize_data_hub_organization_id_string, hmac_param_name_to_plain_field_name } from './action_request_helpers';
import {
	data_hub_privacy_policy_allows_browser_storage,
	normalize_data_hub_privacy_policy_string,
} from './data_hub_privacy_policy';
import { allowed_all_hmac_key_names, white_list_outgoing_person_fields } from './static_vars';
import { merge_user_data_browser } from './user_data_smart_merge';

// Mirror data-hub-action's `Cache-Control: private, max-age=21600` for the
// cookie-only GET /api/get/user_jwt path. The JWT itself can live for about a
// year, so the frontend needs this shorter freshness cap to avoid skipping the
// server for months.
export const organization_jwt_network_skip_ttl_ms = 6 * 60 * 60 * 1000;

// Keep wide localStorage user_data around longer than the JWT network-skip gate.
// This slot is used for prefill fields the JWT may not carry; fresh GET responses
// still merge over it whenever the six-hour JWT refresh gate opens. Safari/WebKit
// can evict script-writeable storage sooner under ITP, but this app-level TTL lets
// Chrome and other longer-lived storage contexts keep prefill data for up to a year.
export const organization_user_data_local_storage_ttl_ms = 365 * 24 * 60 * 60 * 1000;

export const necessary_browser_storage_user_data_fields = [
	'global_uuid',
	'global_uuid_source',
	'uuids',
];

// Clock-skew buffer when treating a JWT as "still valid" — guards against a JWT that
// expires mid-request being treated as fresh and then 401-ing on the next call.
export const unverified_jwt_expiration_buffer_ms = 5 * 60 * 1000;

/**
 * @param {string|null|undefined} organization_id
 * @returns {string|null}
 */
export function get_organization_jwt_local_storage_key(organization_id) {
	const normalized = normalize_data_hub_organization_id_string(organization_id);
	if (!normalized) {
		return null;
	}
	return `data-hub-user-data:${normalized}`;
}

/**
 * @param {string} jwt_string
 * @returns {Object|null} Unverified `payload.data` from JWT or null.
 */
export function decode_unverified_jwt_data_payload(jwt_string) {
	if (!jwt_string || typeof jwt_string !== 'string') {
		return null;
	}
	const segments = jwt_string.split('.');
	if (segments.length < 2) {
		return null;
	}
	try {
		let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
		const pad = base64.length % 4;
		if (pad) {
			base64 += '='.repeat(4 - pad);
		}
		const json_payload = JSON.parse(atob(base64));
		const data = json_payload?.data;
		if (!data || typeof data !== 'object' || Array.isArray(data)) {
			return null;
		}
		return data;
	} catch {
		return null;
	}
}

/**
 * @param {string} jwt_string
 * @returns {number|null} JWT `iat` in milliseconds or null.
 */
export function read_unverified_jwt_issued_at_ms(jwt_string) {
	if (!jwt_string || typeof jwt_string !== 'string') {
		return null;
	}
	const segments = jwt_string.split('.');
	if (segments.length < 2) {
		return null;
	}
	try {
		let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
		const pad = base64.length % 4;
		if (pad) {
			base64 += '='.repeat(4 - pad);
		}
		const json_payload = JSON.parse(atob(base64));
		const iat = json_payload?.iat;
		if (typeof iat !== 'number' || !Number.isFinite(iat)) {
			return null;
		}
		return iat * 1000;
	} catch {
		return null;
	}
}

/**
 * @param {string} jwt_string
 * @returns {number|null} JWT `exp` in milliseconds or null.
 */
export function read_unverified_jwt_expiration_ms(jwt_string) {
	if (!jwt_string || typeof jwt_string !== 'string') {
		return null;
	}
	const segments = jwt_string.split('.');
	if (segments.length < 2) {
		return null;
	}
	try {
		let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
		const pad = base64.length % 4;
		if (pad) {
			base64 += '='.repeat(4 - pad);
		}
		const json_payload = JSON.parse(atob(base64));
		const exp = json_payload?.exp;
		if (typeof exp !== 'number' || !Number.isFinite(exp)) {
			return null;
		}
		return exp * 1000;
	} catch {
		return null;
	}
}

/**
 * @param {string} jwt_string
 * @param {number} [buffer_ms]
 * @returns {boolean} True when JWT has an `exp` claim and now < exp - buffer.
 */
export function is_unverified_jwt_not_expired(jwt_string, buffer_ms = unverified_jwt_expiration_buffer_ms) {
	const exp_ms = read_unverified_jwt_expiration_ms(jwt_string);
	if (exp_ms === null) {
		return false;
	}
	return Date.now() < exp_ms - buffer_ms;
}

/**
 * @param {Object} cache_entry
 * @param {number} [ttl_ms]
 * @returns {boolean}
 */
export function is_organization_jwt_cache_entry_within_ttl(cache_entry, ttl_ms = organization_jwt_network_skip_ttl_ms) {
	if (!cache_entry || typeof cache_entry !== 'object') {
		return false;
	}
	const now = Date.now();
	const jwt_updated_at = typeof cache_entry.jwt_updated_at === 'number' ? cache_entry.jwt_updated_at : null;
	const updated_at = typeof cache_entry.updated_at === 'number' ? cache_entry.updated_at : null;
	const jwt_fallback = typeof cache_entry.jwt === 'string' ? read_unverified_jwt_issued_at_ms(cache_entry.jwt) : null;
	const reference_time = jwt_updated_at ?? updated_at ?? jwt_fallback;
	if (reference_time === null || reference_time === undefined) {
		return false;
	}
	return now - reference_time < ttl_ms;
}

function get_cache_entry_jwt_updated_at_ms(cache_entry) {
	if (!cache_entry || typeof cache_entry !== 'object') {
		return null;
	}
	if (typeof cache_entry.jwt_updated_at === 'number') {
		return cache_entry.jwt_updated_at;
	}
	if (typeof cache_entry.updated_at === 'number') {
		return cache_entry.updated_at;
	}
	if (typeof cache_entry.jwt === 'string') {
		return read_unverified_jwt_issued_at_ms(cache_entry.jwt);
	}
	return null;
}

function get_cache_entry_user_data_updated_at_ms(cache_entry) {
	if (!cache_entry || typeof cache_entry !== 'object') {
		return null;
	}
	if (typeof cache_entry.user_data_updated_at === 'number') {
		return cache_entry.user_data_updated_at;
	}
	if (typeof cache_entry.updated_at === 'number') {
		return cache_entry.updated_at;
	}
	return null;
}

function is_organization_user_data_cache_entry_within_ttl(cache_entry, ttl_ms = organization_user_data_local_storage_ttl_ms) {
	const reference_time = get_cache_entry_user_data_updated_at_ms(cache_entry);
	if (reference_time === null || reference_time === undefined) {
		return false;
	}
	return Date.now() - reference_time < ttl_ms;
}

function read_cache_entry_from_local_storage_key(key) {
	if (!key || typeof localStorage === 'undefined') {
		return null;
	}
	try {
		const raw = localStorage.getItem(key);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') {
			return null;
		}
		// `jwt` and `user_data` are independently optional — an entry that only carries
		// wide user_data from a recent submit (server didn't echo a JWT) is still valid.
		// Callers that require a JWT specifically check entry.jwt themselves.
		const has_jwt = typeof parsed.jwt === 'string' && parsed.jwt.length > 0;
		const has_user_data = parsed.user_data && typeof parsed.user_data === 'object' && !Array.isArray(parsed.user_data);
		if (!has_jwt && !has_user_data) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

/**
 * @param {string|null|undefined} organization_id
 * @returns {Object|null}
 */
export function read_organization_jwt_cache_entry(organization_id) {
	const key = get_organization_jwt_local_storage_key(organization_id);
	return read_cache_entry_from_local_storage_key(key);
}

/**
 * Read the cached user_data payload for an org, gated by TTL and filtered through
 * the current browser storage privacy policy. Returns {} when missing/stale so
 * callers can blindly merge without null checks.
 *
 * @param {string|null|undefined} organization_id
 * @param {Object|null} data_hub_instance
 * @returns {Object} user_data object (possibly empty).
 */
export function read_fresh_organization_user_data(organization_id, data_hub_instance = null) {
	const entry = read_organization_jwt_cache_entry(organization_id);
	if (!entry || !is_organization_user_data_cache_entry_within_ttl(entry)) {
		return {};
	}
	if (!entry.user_data || typeof entry.user_data !== 'object' || Array.isArray(entry.user_data)) {
		return {};
	}
	return filter_user_data_to_outgoing_whitelist(entry.user_data, data_hub_instance);
}

/**
 * @param {string|null|undefined} organization_id
 */
export function remove_organization_jwt_cache_entry(organization_id) {
	const key = get_organization_jwt_local_storage_key(organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return;
	}
	try {
		localStorage.removeItem(key);
	} catch {
		// storage blocked
	}
}

/**
 * @param {Object} data_hub_instance
 * @param {string} jwt_string
 */
export function write_organization_jwt_cache_entry(data_hub_instance, jwt_string) {
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return;
	}
	if (!jwt_string || typeof jwt_string !== 'string') {
		return;
	}
	const key = get_organization_jwt_local_storage_key(data_hub_instance?.organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return;
	}
	const now = Date.now();
	try {
		const previous = read_organization_jwt_cache_entry(data_hub_instance?.organization_id);
		const previous_user_data_is_fresh = (
			previous?.user_data &&
			typeof previous.user_data === 'object' &&
			!Array.isArray(previous.user_data) &&
			is_organization_user_data_cache_entry_within_ttl(previous)
		);
		const previous_jwt_is_fresh = Boolean(
			previous?.jwt &&
			typeof previous.jwt === 'string' &&
			previous.jwt.length > 0 &&
			is_organization_jwt_cache_entry_within_ttl(previous) &&
			is_unverified_jwt_not_expired(previous.jwt)
		);
		const created_at =
			previous &&
			(previous_user_data_is_fresh || previous_jwt_is_fresh) &&
			typeof previous.created_at === 'number'
				? previous.created_at
				: now;

		const entry = {
			jwt: jwt_string,
			created_at,
			updated_at: now,
			jwt_updated_at: now,
		};
		// Preserve any wide user_data already written for this org. JWT writes and
		// user_data writes target the same key but the fields are independent — without
		// this, a fresh JWT response from /api/get/user_jwt would wipe the rich
		// user_data we just persisted from a form submit. The separate timestamp keeps
		// JWT refreshes from extending stale user_data forever.
		if (previous_user_data_is_fresh) {
			const previous_user_data_for_current_policy = filter_user_data_to_outgoing_whitelist(
				previous.user_data,
				data_hub_instance
			);
			if (Object.keys(previous_user_data_for_current_policy).length > 0) {
				entry.user_data = previous_user_data_for_current_policy;
				entry.user_data_updated_at = get_cache_entry_user_data_updated_at_ms(previous);
			}
		}
		localStorage.setItem(key, JSON.stringify(entry));
	} catch {
		// quota / private mode
	}
}

/**
 * Filters cached browser user_data according to the active storage privacy policy.
 * `all` keeps the normal outgoing person whitelist, while `necessary` keeps only the
 * identity fields needed to preserve the org-scoped UUID/JWT relationship.
 *
 * @param {Object|null|undefined} user_data_candidate
 * @param {Object} data_hub_instance
 * @returns {Object}
 */
function filter_user_data_to_outgoing_whitelist(user_data_candidate, data_hub_instance = window?.data_hub) {
	if (
		!user_data_candidate
		|| typeof user_data_candidate !== 'object'
		|| Array.isArray(user_data_candidate)
	) {
		return {};
	}
	const privacy_policy = normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy);
	let allowed_user_data_fields = white_list_outgoing_person_fields;
	if (privacy_policy === 'necessary') {
		allowed_user_data_fields = necessary_browser_storage_user_data_fields;
	}
	const filtered = {};
	Object.keys(user_data_candidate).forEach((key) => {
		if (allowed_user_data_fields.includes(key)) {
			filtered[key] = user_data_candidate[key];
		}
	});
	return filtered;
}

/**
 * Merge `incoming_user_data` onto the existing cache entry's user_data field and persist.
 * Preserves the entry's `jwt` field. Skips silently when privacy disallows storage, when
 * there's no organization_id, or when `incoming_user_data` has no whitelist-allowed keys.
 *
 * Merge precedence: incoming wins on conflict (it's the fresher source — typically a
 * just-completed form submit response or GET /api/get/user_jwt response body). complexKeys
 * (emails, phone_numbers, uuids) dedupe via merge_user_data_browser; listArrayKeys merge
 * by unique ID so different browser/cache sources can contribute known subscriptions.
 *
 * @param {Object} data_hub_instance
 * @param {Object} incoming_user_data
 * @returns {Promise<Object|null>} The merged user_data that was persisted, or null when skipped.
 */
export async function write_organization_user_data_cache_entry(data_hub_instance, incoming_user_data) {
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return null;
	}
	const filtered_incoming = filter_user_data_to_outgoing_whitelist(incoming_user_data, data_hub_instance);
	if (Object.keys(filtered_incoming).length === 0) {
		return null;
	}
	const key = get_organization_jwt_local_storage_key(data_hub_instance?.organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return null;
	}
	const previous = read_organization_jwt_cache_entry(data_hub_instance?.organization_id);
	const previous_user_data_is_fresh = (
		previous?.user_data &&
		typeof previous.user_data === 'object' &&
		!Array.isArray(previous.user_data) &&
		is_organization_user_data_cache_entry_within_ttl(previous)
	);
	const previous_jwt_is_fresh = (
		previous?.jwt &&
		typeof previous.jwt === 'string' &&
		previous.jwt.length > 0 &&
		is_organization_jwt_cache_entry_within_ttl(previous) &&
		is_unverified_jwt_not_expired(previous.jwt)
	);
	let previous_user_data = {};
	if (previous_user_data_is_fresh) {
		previous_user_data = filter_user_data_to_outgoing_whitelist(previous.user_data, data_hub_instance);
	}
	const now = Date.now();
	const created_at =
		previous &&
		(previous_user_data_is_fresh || previous_jwt_is_fresh) &&
		typeof previous.created_at === 'number'
			? previous.created_at
			: now;
	const merged_user_data = await merge_user_data_browser(previous_user_data, filtered_incoming);
	const entry = {
		user_data: merged_user_data,
		created_at,
		updated_at: now,
		user_data_updated_at: now,
	};
	// Preserve any JWT already cached for this org (independent field).
	if (previous_jwt_is_fresh) {
		entry.jwt = previous.jwt;
		entry.jwt_updated_at = get_cache_entry_jwt_updated_at_ms(previous);
	}
	try {
		localStorage.setItem(key, JSON.stringify(entry));
	} catch {
		// quota / private mode
		return null;
	}
	return merged_user_data;
}

/**
 * @param {Object} data_hub_instance
 * @returns {boolean}
 */
export function has_hmac_key_pair_for_user_jwt_fetch(data_hub_instance) {
	if (!data_hub_instance?.get_iterable_user_data_via_hmac_key) {
		return false;
	}
	const url_params = data_hub_instance.url_params;
	if (!(url_params instanceof URLSearchParams)) {
		return false;
	}
	const plain_bound = new Set();
	for (const hmac_key_name of allowed_all_hmac_key_names) {
		const plain_field_name = hmac_param_name_to_plain_field_name(hmac_key_name);
		const hmac_hash = url_params.has(hmac_key_name)
			? url_params.get(hmac_key_name)
			: data_hub_instance.user_data?.[hmac_key_name] || null;
		if (!hmac_hash) {
			continue;
		}
		const identifier = url_params.has(plain_field_name)
			? url_params.get(plain_field_name)
			: data_hub_instance.user_data?.[plain_field_name] || null;
		if (!identifier) {
			continue;
		}
		if (plain_bound.has(plain_field_name)) {
			continue;
		}
		plain_bound.add(plain_field_name);
		return true;
	}
	return false;
}

/**
 * @param {Object} data_hub_instance
 * @returns {boolean}
 */
export function should_skip_network_fetch_for_cached_organization_jwt(data_hub_instance) {
	if (!data_hub_instance) {
		return false;
	}
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return false;
	}
	if (has_hmac_key_pair_for_user_jwt_fetch(data_hub_instance)) {
		return false;
	}
	const org_id = data_hub_instance.organization_id;
	const entry = read_organization_jwt_cache_entry(org_id);
	if (!entry?.jwt) {
		return false;
	}
	if (!is_organization_jwt_cache_entry_within_ttl(entry)) {
		return false;
	}
	if (!is_unverified_jwt_not_expired(entry.jwt)) {
		return false;
	}
	const data = decode_unverified_jwt_data_payload(entry.jwt);
	if (!data?.global_uuid || typeof data.global_uuid !== 'string') {
		return false;
	}
	// A minted unknown_uuid is still a valid browser tracking identity for the current
	// session. Do not refetch on every page view just because the source is unknown_uuid;
	// the six-hour JWT freshness cap and explicit HMAC links are the refresh gates.
	return true;
}
